import { memo } from "react";

import { TableCell } from "@/components/ui/Table";

const tableCellReducedHeightClassName = "px-4 py-3 font-medium"; // Slightly reduced row height for better density

import { SearchableSelectCell } from "./cells/SearchableSelectCell";
import { SelectCell } from "./cells/SelectCell";
import type { EditableTableCellProps } from "./types";

/**
 * Cell component for EditableTable
 * Renders appropriate editor based on column config, or displays static value
 *
 * Memoized to prevent unnecessary re-renders when other cells are edited
 */
function EditableTableCellComponent<TData>({
  column,
  value,
  isEdited,
  onChange,
  row,
}: EditableTableCellProps<TData>) {
  // Non-editable cell - just display the value
  if (!column.editable) {
    const displayValue: string | number = column.format
      ? column.format(value)
      : String(value ?? "-");
    return (
      <TableCell className={tableCellReducedHeightClassName}>
        {displayValue}
      </TableCell>
    );
  }

  const stringValue = String(value || "");

  if (column.editor === "select" && column.options) {
    return (
      <TableCell className={tableCellReducedHeightClassName}>
        <SelectCell
          value={stringValue}
          onChange={onChange}
          options={column.options}
          isEdited={isEdited}
          formatter={column.format}
        />
      </TableCell>
    );
  }

  if (
    column.editor === "searchable-select" &&
    (column.options || column.getOptions)
  ) {
    // Use dynamic options if getOptions is provided, otherwise use static options
    const options =
      column.getOptions && row ? column.getOptions(row) : column.options || [];
    return (
      <TableCell className={tableCellReducedHeightClassName}>
        <SearchableSelectCell
          value={stringValue}
          onChange={onChange}
          options={options}
          isEdited={isEdited}
          placeholder="Select..."
        />
      </TableCell>
    );
  }

  return (
    <TableCell className={tableCellReducedHeightClassName}>
      {stringValue || "-"}
    </TableCell>
  );
}

/**
 * Memoized version to prevent re-renders of unchanged cells
 * Only re-renders when value or isEdited changes
 */
export const EditableTableCell = memo(
  EditableTableCellComponent,
  (prevProps, nextProps) => {
    return (
      prevProps.value === nextProps.value &&
      prevProps.isEdited === nextProps.isEdited &&
      prevProps.column === nextProps.column
    );
  },
) as typeof EditableTableCellComponent;
